import React, { useState, useEffect, useRef } from "react";
import { Modal, ModalHeader, ModalBody, Spinner } from "reactstrap";
import firebase from "config/firebase";

// Formats remaining time from a Firestore Timestamp
const formatTimeLeft = (expiryTimestamp) => {
  const diffMs = expiryTimestamp.toDate().getTime() - Date.now();
  if (diffMs <= 0) return null;
  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  return `${days}d ${hours}h left`;
};

// Single opinion card with expand/collapse
const OpinionCard = ({ fanName, opinion, timeLeft }) => {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    if (textRef.current) {
      setOverflows(textRef.current.scrollHeight > textRef.current.clientHeight);
    }
  }, [opinion]);

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <span style={styles.fanName}>{fanName}</span>
        {timeLeft && <span style={styles.timeLeft}>{timeLeft}</span>}
      </div>
      <p
        ref={textRef}
        style={{
          ...styles.opinionText,
          ...(!expanded ? styles.clamped : {}),
        }}
      >
        {opinion}
      </p>
      {(overflows || expanded) && (
        <span style={styles.readMore} onClick={() => setExpanded((v) => !v)}>
          {expanded ? "Show less" : "Read more"}
        </span>
      )}
    </div>
  );
};

const FanOpinionsModal = ({ isOpen, toggle, boostDocId, latestOpinion }) => {
  const [opinions, setOpinions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !boostDocId) return;

    setLoading(true);
    const now = firebase.firestore.Timestamp.now();

    const unsub = firebase
      .firestore()
      .collection("boosts")
      .doc(boostDocId)
      .collection("opinions")
      .where("expiryTime", ">", now)
      .onSnapshot(
        (snap) => {
          if (!snap.empty) {
            const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setOpinions(docs);
          } else if (latestOpinion) {
            // Legacy fallback: parent doc's latestOpinion field
            setOpinions([{ id: "legacy", opinion: latestOpinion, fanName: "Fan" }]);
          } else {
            setOpinions([]);
          }
          setLoading(false);
        },
        (err) => {
          console.error("FanOpinionsModal error", err);
          setLoading(false);
        }
      );

    return () => unsub();
  }, [isOpen, boostDocId, latestOpinion]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered scrollable>
      <ModalHeader toggle={toggle} style={{ paddingBottom: 4, borderBottom: "none" }}>
        Fan Opinions
      </ModalHeader>
      <ModalBody style={{ paddingTop: 4, maxHeight: 420 }}>
        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
          </div>
        ) : opinions.length === 0 ? (
          <p style={{ color: "#8898aa", textAlign: "center", marginTop: 16 }}>
            No active opinions.
          </p>
        ) : (
          opinions.map((op) => (
            <OpinionCard
              key={op.id}
              fanName={op.fanName || "Fan"}
              opinion={op.opinion}
              timeLeft={op.expiryTime ? formatTimeLeft(op.expiryTime) : null}
            />
          ))
        )}
      </ModalBody>
    </Modal>
  );
};

export default FanOpinionsModal;

const styles = {
  card: {
    background: "#f8f9fa",
    borderRadius: 10,
    padding: "12px 14px",
    marginBottom: 12,
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  fanName: {
    fontWeight: 700,
    fontSize: 13,
    color: "#32325d",
  },
  timeLeft: {
    fontSize: 11,
    color: "#5e72e4",
    fontWeight: 600,
    background: "#eef0fd",
    borderRadius: 20,
    padding: "2px 8px",
  },
  opinionText: {
    fontSize: 13,
    color: "#525f7f",
    margin: 0,
    lineHeight: "1.5",
  },
  clamped: {
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  readMore: {
    fontSize: 12,
    color: "#5e72e4",
    cursor: "pointer",
    fontWeight: 600,
    display: "inline-block",
    marginTop: 4,
  },
};
